/**
 * PresentCardListContainer
 * --
 */
import React from 'react';
import PresentCardListPresenter from './PresentCardListPresenter';

import {
  useGetPresentCards,
  useCreatePresentCard,
  useDeletePresentCard,
  useUpsertPresentCard,
  useUpdatePresentCard,
  presentCardQueryKeys,
} from 'hooks/PresentCardHooks';
import MessageAlert from 'utils';
import { useQueryClient } from '@tanstack/react-query';
import { FileManager } from 'utils/FileManager';

const INITIAL_VALUE = {
  title: '',
  category_name: '',
  is_show: 'Y',
  order: null,
  image_url: '',
};

const PresentCardListContainer = () => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  /* ===== Mutate ===== */
  const queryClient = useQueryClient();
  const { createPresentCard } = useCreatePresentCard();
  const { deletePresentCard } = useDeletePresentCard();
  const { updatePresentCard } = useUpdatePresentCard();
  const { upsertPresentCard } = useUpsertPresentCard();

  /* ===== Data ===== */
  const { data: PresentCards } = useGetPresentCards(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) {
        return [];
      }

      return data;
    },
    initialData: [],
  });

  /* ===== STATE ===== */
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [isLoading, setIsLoading] = React.useState(false);
  const [modalType, setModalType] = React.useState(null);
  const [dataList, setDataList] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   *
   * @param {string} t
   * @param {string | number} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 모달 핸들러
   * --
   * @param {string} t
   * @param {object | null | array} data
   */
  const handleModal = (t = 'close', data = null) => {
    const type = t.toLocaleUpperCase();

    setIsLoading(true);

    switch (type) {
      case 'CREATE':
        setInputValue(INITIAL_VALUE);

        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'UPDATE':
      case 'DETAIL':
        setInputValue(data);

        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'SORT':
        setDataList(data);
        setTimeout(() => {
          setModalType(type);
          setIsLoading(false);
        }, 1000);
        break;
      case 'CLOSE':
      default:
        setInputValue(INITIAL_VALUE);
        setModalType(null);
        setIsLoading(false);
        break;
    }
  };

  /**
   * 배너 등록 유효성 검사
   * --
   */
  const handleValidateCheck = () => {
    const { title, category_name, image_url } = inputValue;

    if (!title) {
      MessageAlert.info('타이틀을 입력해주세요!');
      return false;
    }
    if (!category_name) {
      MessageAlert.info('카테고리명을 입력해주세요!');
      return false;
    }

    if (!image_url) {
      MessageAlert.info('카드 이미지를 등록해주세요!');
      return false;
    }

    return true;
  };

  /**
   * 배너 등록/수정/삭제
   * --
   * @param {string} t
   * @param {array} arr
   */
  const handleSubmit = async (t, arr = []) => {
    const submitType = t.toLocaleUpperCase();

    const confirm = (msg = '') => window.confirm(msg);

    try {
      setIsLoading(true);

      const { image_url } = inputValue;
      let res;
      let newList;
      switch (submitType) {
        case 'CREATE': // 배너 추가
          if (!handleValidateCheck()) return;

          if (!confirm('선물 카드를 등록하시겠습니까?')) return;

          const upload = await FileManager.upload('present/card', image_url);

          if (upload.status !== 200) {
            MessageAlert.info('파일 업로드에 실패하였습니다.');
            return;
          }

          const createData = {
            ...inputValue,
            image_url: upload.data.file_name,
            order: PresentCards.length + 1,
          };

          res = await createPresentCard(createData);

          if (res.status !== 200) {
            MessageAlert.info('선물 카드 등록에 실패하였습니다.');
            return;
          }

          MessageAlert.success('선물 카드가 성공적으로 등록되었습니다.');
          break;

        case 'UPDATE': // 배너 수정
          if (!handleValidateCheck()) return;

          if (!confirm('선물 카드를 수정하시겠습니까?')) return;

          let presentCardURL = image_url;
          if (typeof presentCardURL !== 'string') {
            const updateUpload = await FileManager.upload(
              'present/card',
              presentCardURL
            );

            if (updateUpload.status !== 200) {
              MessageAlert.info('파일 업로드에 실패하였습니다.');
              return;
            }
            presentCardURL = updateUpload?.data?.file_name;
          }

          const updateData = {
            ...inputValue,
            image_url: presentCardURL,
          };

          res = await updatePresentCard(updateData);

          if (res.status !== 200) {
            MessageAlert.info('선물 카드 수정에 실패하였습니다.');
            return;
          }

          MessageAlert.success('선물 카드가 성공적으로 수정되었습니다.');
          break;

        case 'DELETE': // 배너 삭제
          if (!confirm('선물 카드를 정말 삭제하시겠습니까?')) return;

          res = await deletePresentCard(inputValue.present_card_id);

          if (res.status !== 200) {
            MessageAlert.info('선물 카드 삭제에 실패하였습니다.');
            return;
          }

          const removePresentCards = PresentCards.filter(
            (present_card) =>
              present_card.present_card_id !== inputValue.present_card_id
          );

          newList = removePresentCards.map((present_card, index) => ({
            ...present_card,
            order: index + 1,
          }));
          await upsertPresentCard(newList);

          MessageAlert.success('선물카드가 성공적으로 삭제되었습니다.');
          break;

        case 'SORT': // 순서 변경
          if (!confirm('선물카드 순서를 변경하시겠습니까?')) return;
          const newArr = arr.map((item, index) => ({
            ...item,
            order: index + 1,
          }));
          res = await upsertPresentCard(newArr);

          if (res.status !== 200) {
            MessageAlert.info('순서변경에 실패하였습니다.');
            return;
          }

          MessageAlert.success('순서가 성공적으로 변경되었습니다.');
          break;

        default:
          MessageAlert.info('선물카드 관리 실패!');
          break;
      }

      // NOTE: 데이터 최신화
      const queryKey = presentCardQueryKeys.getPresentCards(null);
      queryClient.invalidateQueries({ queryKey: queryKey.queryKey });

      setModalType(null);
    } catch (err) {
      console.error('[handleSubmit] Error!! ', err);
      MessageAlert.error(
        `선물하기 ${
          submitType === 'CREATE'
            ? '생성'
            : submitType === 'UPDATE'
            ? '수정'
            : '삭제'
        }에 실패하였습니다!`
      );
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 1000);
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 선뭃 데이터 정제
   * --
   */
  const PresentCardList = React.useMemo(() => {
    if (!PresentCards) return [];

    const newData = PresentCards.map((present_card) => ({
      key: present_card.present_card_id,
      ...present_card,
    }));

    const sortData = newData.sort(
      (a, b) => b.present_card_id - a.present_card_id
    );

    return sortData;
  }, [PresentCards]);
  /* ===== RENDER ===== */
  return (
    <>
      <PresentCardListPresenter
        presentCards={PresentCardList}
        inputValue={inputValue}
        isLoading={isLoading}
        onChange={handleChange}
        onSubmit={handleSubmit}
        setIsLoading={setIsLoading}
        modalType={modalType}
        onModal={handleModal}
        dataList={dataList}
        setDataList={setDataList}
      />
    </>
  );
};

export default PresentCardListContainer;
